import { GRID_COLUMNS, type LayoutItem } from '@vision360/contracts';
import {
  cloneLayout,
  type LayoutItem as GridItem,
  type Layout,
  moveElement,
  verticalCompactor,
} from 'react-grid-layout';
import type { AnyWidget, Size } from '@/widgets/_sdk/types';

/**
 * Pure layout operations (TechSpec §9.5). The persisted shape is the
 * contracts' LayoutItem (geometry + widget); react-grid-layout sees geometry.
 */

const FALLBACK_MIN: Size = { w: 1, h: 1 };

export function minSizeOf(item: LayoutItem, registry: ReadonlyMap<string, AnyWidget>): Size {
  return registry.get(item.widget.type)?.minSize ?? FALLBACK_MIN;
}

export function toGrid(
  items: readonly LayoutItem[],
  registry: ReadonlyMap<string, AnyWidget>,
): GridItem[] {
  return items.map((item) => {
    const min = minSizeOf(item, registry);
    return { i: item.i, x: item.x, y: item.y, w: item.w, h: item.h, minW: min.w, minH: min.h };
  });
}

/** New geometry onto the items; the same array when nothing moved. */
export function mergeGeometry(items: LayoutItem[], grid: Layout): LayoutItem[] {
  const byId = new Map(grid.map((g) => [g.i, g]));
  let changed = false;
  const next = items.map((item) => {
    const g = byId.get(item.i);
    if (!g || (g.x === item.x && g.y === item.y && g.w === item.w && g.h === item.h)) return item;
    changed = true;
    return { ...item, x: g.x, y: g.y, w: g.w, h: g.h };
  });
  return changed ? next : items;
}

function compacted(items: LayoutItem[], grid: GridItem[]): LayoutItem[] {
  return mergeGeometry(items, verticalCompactor.compact(grid, GRID_COLUMNS));
}

/**
 * Arrow keys in edit mode: one cell, pushing others aside like a drag would.
 * Vertically, compaction would pull a widget straight back into the gap it
 * left, so it travels until it actually lands somewhere new (past its neighbour).
 */
export function keyboardMove(
  items: LayoutItem[],
  id: string,
  dx: number,
  dy: number,
  registry: ReadonlyMap<string, AnyWidget>,
): LayoutItem[] {
  const origin = items.find((i) => i.i === id);
  if (!origin) return items;
  const bottom = items.reduce((max, i) => Math.max(max, i.y + i.h), 0);
  const x = Math.min(Math.max(0, origin.x + dx), GRID_COLUMNS - origin.w);
  for (let step = 1; step <= Math.max(1, bottom); step++) {
    const y = Math.max(0, origin.y + dy * step);
    const grid = cloneLayout(toGrid(items, registry)) as GridItem[];
    const target = grid.find((g) => g.i === id) as GridItem;
    const moved = moveElement(grid, target, x, y, true, false, 'vertical', GRID_COLUMNS);
    const next = compacted(items, [...moved]);
    const landed = next.find((i) => i.i === id);
    if (next !== items && landed && (landed.x !== origin.x || landed.y !== origin.y)) return next;
    if (dy === 0 || y === 0) return next;
  }
  return items;
}

/** Shift + arrow keys: one cell wider/narrower, taller/shorter, within min size and grid. */
export function keyboardResize(
  items: LayoutItem[],
  id: string,
  dw: number,
  dh: number,
  registry: ReadonlyMap<string, AnyWidget>,
): LayoutItem[] {
  const item = items.find((i) => i.i === id);
  if (!item) return items;
  const min = minSizeOf(item, registry);
  const w = Math.min(Math.max(min.w, item.w + dw), GRID_COLUMNS - item.x);
  const h = Math.min(Math.max(min.h, item.h + dh), 100);
  if (w === item.w && h === item.h) return items;
  const next = items.map((i) => (i.i === id ? { ...i, w, h } : i));
  return compacted(next, toGrid(next, registry));
}

/** A new widget goes below everything, at the left. */
export function placeNew(
  items: readonly LayoutItem[],
  size: Size,
): Pick<LayoutItem, 'x' | 'y' | 'w' | 'h'> {
  const bottom = items.reduce((max, i) => Math.max(max, i.y + i.h), 0);
  return { x: 0, y: bottom, w: Math.min(size.w, GRID_COLUMNS), h: size.h };
}

/** Reading order for the narrow single-column view: rows, then columns. */
export const readingOrder = (items: readonly LayoutItem[]): LayoutItem[] =>
  [...items].sort((a, b) => a.y - b.y || a.x - b.x);
